/**
 * RDX Wave-2 — Resolver Snapshot subsection for Site Audit tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { SiteEntity } from '../../../types/site';
import type { ResolvedFieldRecord } from '../../../lib/resolvers/types';
import {
  findConflictsAcrossRegistries,
  findTier3DominatedFields,
  getAllResolvedFields,
} from '../../../lib/resolvers/compositeIndex';
import { useStore } from '../../../store';

export function ResolverSnapshotPanel({ site }: { site: SiteEntity }) {
  const allReports = useStore((s) => s.siteRag.reports);
  const allAmendments = useStore((s) => s.amendmentState.amendments);
  const reports = useMemo(
    () => allReports.filter((r) => r.siteId === site.id),
    [allReports, site.id],
  );
  const amendments = useMemo(
    () => allAmendments.filter((a) => a.sedId === site.sedId),
    [allAmendments, site.sedId],
  );
  const [records, setRecords] = useState<ResolvedFieldRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void getAllResolvedFields({
      site,
      reports,
      amendments,
    }).then((rows) => {
      if (!cancelled) {
        setRecords(rows);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [site, reports, amendments]);

  const tier3 = useMemo(() => findTier3DominatedFields(records), [records]);
  const crossConflicts = useMemo(() => findConflictsAcrossRegistries(records), [records]);

  if (loading) {
    return (
      <section className="border border-border rounded-lg p-4" data-testid="resolver-snapshot-panel">
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading resolver snapshot…
        </div>
      </section>
    );
  }

  return (
    <section
      className="border border-border rounded-lg p-4 space-y-4"
      data-testid="resolver-snapshot-panel"
      aria-label="Resolver Snapshot"
    >
      <div>
        <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary">
          Resolver Snapshot
        </h3>
        <p className="text-xs text-text-muted mt-1">
          Cross-registry field resolution — tier-3-only and cross-registry conflicts.
        </p>
      </div>

      {tier3.length > 0 && (
        <div data-testid="resolver-tier3-warnings" className="space-y-2">
          <p className="text-xs font-semibold text-amber-600 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            Tier 3-dominated fields ({tier3.length})
          </p>
          <ul className="text-xs space-y-1">
            {tier3.slice(0, 8).map((r) => (
              <li key={`${r.registryId}:${r.fieldKey}`} className="text-amber-700">
                {r.registryId} · {r.fieldKey} — LLM inference only
              </li>
            ))}
          </ul>
        </div>
      )}

      {crossConflicts.length > 0 && (
        <div data-testid="resolver-cross-conflicts" className="space-y-2">
          <p className="text-xs font-semibold text-red-600 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            Cross-registry conflicts ({crossConflicts.length})
          </p>
          <ul className="text-xs space-y-1">
            {crossConflicts.slice(0, 8).map((c) => (
              <li key={c.fieldKey} className="text-red-700">
                {c.fieldKey}: {c.registries.map((g) => g.registryId).join(' vs ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tier3.length === 0 && crossConflicts.length === 0 && (
        <p className="text-xs text-text-secondary" data-testid="resolver-snapshot-clean">
          {records.length} resolved fields across 10 registries — no tier-3 or cross-registry issues.
        </p>
      )}
    </section>
  );
}
